import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MemberPaymentDetails, MemberSubscriptionDetails } from '../api/client';

interface SubscriptionAccessOverlayProps {
  children: React.ReactNode;
  subscription: MemberSubscriptionDetails | null;
  latestPayment?: MemberPaymentDetails | null;
  isExpired?: boolean;
  subscriptionError?: string;
}

function formatDate(value?: string | null) {
  if (!value) {
    return '-';
  }

  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatShortId(value?: string | null) {
  if (!value) return '-';
  const v = String(value).trim();
  if (v.length <= 20) return v;
  return `${v.slice(0, 8)}…${v.slice(-6)}`;
}
export default function SubscriptionAccessOverlay({
  children,
  subscription,
  latestPayment,
  isExpired = false,
  subscriptionError,
}: SubscriptionAccessOverlayProps) {
  const navigate = useNavigate();

  return (
    <Box position="relative">
      <Box filter="blur(1.5px)" opacity={0.9} pointerEvents="none" userSelect="none" aria-hidden="true">
        {children}
      </Box>

      <Center position="absolute" inset={0} px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <Box
          width="full"
          maxW="2xl"
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="2xl"
          boxShadow="2xl"
          p={{ base: 5, md: 7 }}
        >
          <Stack spacing={5}>
            <Stack spacing={2}>
              <Badge width="fit-content" colorScheme={subscription ? 'orange' : 'blue'}>
                {subscription ? 'Subscription Required' : 'Access Locked'}
              </Badge>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" color="gray.900">
                {isExpired ? 'Your subscription has expired' : subscription ? 'Your subscription is not active' : 'Choose a subscription to read articles'}
              </Text>
              <Text color="gray.600">
                {isExpired
                  ? 'This article area is locked because the subscription end date has passed. Review the details below and renew access from your subscription page.'
                  : subscription
                    ? 'This article area stays locked until the subscription becomes active again.'
                    : 'Member articles are available after you activate a subscription plan.'}
              </Text>
            </Stack>

            {subscriptionError ? (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                {subscriptionError}
              </Alert>
            ) : null}

            {subscription ? (
              <Stack spacing={4} p={4} bg="gray.50" borderRadius="xl" borderWidth="1px" borderColor="gray.200">
                <Text fontWeight="700" color="gray.800">Subscription Details</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Text><strong>Plan:</strong> {subscription.plan_name}</Text>
                  <Text><strong>Status:</strong> {subscription.status || '-'}</Text>
                  <Text><strong>Amount:</strong> {subscription.currency} {Number(subscription.amount).toFixed(2)}</Text>
                  <Text><strong>Cycle:</strong> {subscription.billing_cycle}</Text>
                  <Text><strong>Expires At:</strong> {formatDate(subscription.expires_at)}</Text>
                  <Text><strong>Days Left:</strong> {subscription.days_left ?? '-'}</Text>
                </SimpleGrid>

                {latestPayment ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <Text><strong>Latest Payment:</strong> {latestPayment.currency} {Number(latestPayment.amount).toFixed(2)}</Text>
                    <Text><strong>Payment Status:</strong> {latestPayment.status || '-'}</Text>
                      <Text><strong>Reference:</strong> {formatShortId(latestPayment.payment_reference)}</Text>
                    <Text><strong>Paid At:</strong> {formatDate(latestPayment.paid_at)}</Text>
                  </SimpleGrid>
                ) : null}
              </Stack>
            ) : null}

            <Button colorScheme="blue" width={{ base: 'full', md: 'fit-content' }} onClick={() => navigate('/member/subscription')}>
              View Subscription
            </Button>
          </Stack>
        </Box>
      </Center>
    </Box>
  );
}